
import React from "react";
import Layout from "@/components/layout/Layout";
import { ComponentDoc, PropDefinition } from "./ComponentDoc";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ListCheck, FileText, Mail } from "lucide-react";

export default function ComponentsLibrary() {
  // Button component props
  const buttonProps: PropDefinition[] = [
    {
      name: "variant",
      type: "'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'",
      default: "default",
      required: false,
      description: "The visual style of the button"
    },
    {
      name: "size",
      type: "'default' | 'sm' | 'lg' | 'icon'",
      default: "default",
      required: false,
      description: "The size of the button"
    },
    {
      name: "asChild",
      type: "boolean",
      default: "false",
      required: false,
      description: "Whether to render as a child"
    },
    {
      name: "className",
      type: "string",
      required: false,
      description: "Additional CSS classes"
    },
  ];

  // Button code example
  const buttonCode = `import { Button } from "@/components/ui/button";

// Basic usage
<Button>Click me</Button>

// With variants
<Button variant="outline">Outline</Button>
<Button variant="secondary">Secondary</Button>

// With sizes
<Button size="sm">Small Button</Button>
<Button size="lg">Large Button</Button>

// With icon
<Button>
  <ListCheck className="mr-2 h-4 w-4" />
  Button with Icon
</Button>`;

  // Card component props
  const cardProps: PropDefinition[] = [
    {
      name: "className",
      type: "string",
      required: false,
      description: "Additional CSS classes for the card"
    }
  ];

  // Card code example
  const cardCode = `import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Card Description</CardDescription>
  </CardHeader>
  <CardContent>
    <p>Card Content</p>
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>`;

  return (
    <Layout>
      <div className="container py-12">
        <SectionHeader 
          title="Component Library" 
          subtitle="Documentation for the components used throughout the application"
          className="mb-12"
        />

        <div className="space-y-12">
          {/* Button Component */}
          <ComponentDoc
            name="Button"
            description="Buttons trigger actions or events, like submitting a form, opening a dialog, canceling an action, or performing an operation."
            preview={
              <div className="flex flex-wrap gap-4">
                <Button>Default Button</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
              </div>
            }
            propsList={buttonProps}
            code={buttonCode}
            designNotes="Use buttons to trigger actions. The primary button should be used for the main action. Secondary buttons should be used for secondary actions. Outline buttons should be used for less important actions."
            variants={[
              {
                title: "Button with Icon",
                preview: (
                  <Button>
                    <ListCheck className="mr-2 h-4 w-4" />
                    Join Waitlist
                  </Button>
                )
              },
              {
                title: "Outline Button with Icon",
                preview: (
                  <Button variant="outline">
                    <FileText className="mr-2 h-4 w-4" />
                    Download Guide
                  </Button>
                )
              },
              {
                title: "Secondary Button with Icon",
                preview: (
                  <Button variant="secondary">
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                  </Button>
                )
              },
              {
                title: "Disabled Button",
                preview: <Button disabled>Disabled</Button>
              }
            ]}
          />

          {/* Card Component */}
          <ComponentDoc
            name="Card"
            description="Cards are used to group related content and actions. They can contain a header, content, and footer."
            preview={
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle>Card Title</CardTitle>
                  <CardDescription>Card Description</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>This is the card content where the main information goes.</p>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline">Cancel</Button>
                  <Button>Submit</Button>
                </CardFooter>
              </Card>
            }
            propsList={cardProps}
            code={cardCode}
            designNotes="Cards should have consistent padding and spacing. They're commonly used for repeating elements like blog posts, products, or user profiles."
          />

          {/* Input Component */}
          <ComponentDoc
            name="Input"
            description="The Input component is used to get user input in a text field."
            preview={
              <div className="w-full max-w-sm space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" placeholder="Enter your email" type="email" />
                </div>
                <div>
                  <Label htmlFor="disabled">Disabled</Label>
                  <Input id="disabled" value="This input is disabled" disabled />
                </div>
              </div>
            }
            propsList={[
              {
                name: "type",
                type: "string",
                default: "text",
                required: false,
                description: "The input type (text, email, password, etc.)"
              },
              {
                name: "placeholder",
                type: "string",
                required: false,
                description: "Placeholder text"
              },
              {
                name: "disabled",
                type: "boolean",
                default: "false",
                required: false,
                description: "Whether the input is disabled"
              },
            ]}
            code={`import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

<div>
  <Label htmlFor="email">Email</Label>
  <Input id="email" placeholder="Enter your email" type="email" />
</div>`}
          />

          {/* Badge Component */}
          <ComponentDoc
            name="Badge"
            description="Badges are used to highlight an item's status or categorize items."
            preview={
              <div className="flex flex-wrap gap-2">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="outline">Outline</Badge>
                <Badge variant="destructive">Destructive</Badge>
              </div>
            }
            propsList={[
              {
                name: "variant",
                type: "'default' | 'secondary' | 'destructive' | 'outline'",
                default: "default",
                required: false,
                description: "The badge variant"
              },
              {
                name: "className",
                type: "string",
                required: false,
                description: "Additional CSS classes"
              },
            ]}
            code={`import { Badge } from "@/components/ui/badge";

<Badge>Default</Badge>
<Badge variant="secondary">Secondary</Badge>
<Badge variant="outline">Outline</Badge>
<Badge variant="destructive">Destructive</Badge>`}
            designNotes="Use badges sparingly to avoid visual clutter. They're best used to highlight important information that needs to stand out."
          />
        </div>
      </div>
    </Layout>
  );
}
